import React, { ErrorInfo, ReactNode, Suspense } from 'react';

import * as Sentry from '@sentry/react';

import LoadingPage from '@/pages/common/LoadingPage';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Send error to Sentry
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack
        }
      }
    });

    console.error('Uncaught error:', error, errorInfo);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.state.hasError && prevProps.children !== this.props.children) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }

    return <Suspense fallback={<LoadingPage />}>{this.props.children}</Suspense>;
  }
}

// Wrap the ErrorBoundary with Sentry
export default Sentry.withErrorBoundary(ErrorBoundary, {
  showDialog: true,
  dialogOptions: {
    title: 'An Error Has Occurred',
    subtitle: 'Our team has been notified.',
    subtitle2: "If you'd like to help, tell us what happened below.",
    labelName: 'Name',
    labelEmail: 'Email',
    labelComments: 'What happened?',
    labelClose: 'Close',
    labelSubmit: 'Submit',
    errorGeneric: 'An error occurred while submitting your report. Please try again.',
    errorFormEntry: 'Some fields were invalid. Please correct the errors and try again.',
    successMessage: 'Your feedback has been sent. Thank you!'
  }
});
